import React from 'react';
import { X, Share, Plus, MoreVertical, Download } from 'lucide-react';
import { useLanguage } from '../../src/functions/useLanguage';
import { wt } from '../i18n';
import KodaCareLogo from '../../src/components/Custom Icons/KodaCareLogo';

interface InstallModalProps {
  open: boolean;
  onClose: () => void;
  /** The deferred beforeinstallprompt event, if available */
  deferredPrompt: any;
  /** Detected OS string */
  os: string;
  onNativeInstall: () => void;
}

export function InstallModal({ open, onClose, deferredPrompt, os, onNativeInstall }: InstallModalProps) {
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);

  if (!open) return null;

  const isAndroid = os === 'Android';
  const isIOS = os === 'iOS';
  const isDesktop = !isAndroid && !isIOS;
  const canNativeInstall = isAndroid && !!deferredPrompt;

  return (
    // Scrim wrapper
    <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop — purely visual, sits behind the panel */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm cursor-pointer"
        onClick={onClose}
      />
      {/* Panel — z-10 places it above the absolute backdrop; no stopPropagation needed */}
      <div
        className="relative z-10 w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Brown header bar */}
        <div className="bg-primary-brown px-6 pt-6 pb-5 flex items-start justify-between">
          <div>
            <KodaCareLogo style={{ width: 140, height: 25 }} height={25} className="text-white mb-3" />
            <p className="text-white/90 text-sm font-semibold mb-1.5">{t('install.modal.subtitle')}</p>
            <ul className="space-y-0.5">
              {(['install.modal.feature1', 'install.modal.feature2', 'install.modal.feature3'] as const).map(k => (
                <li key={k} className="flex items-center gap-1.5 text-white/85 text-sm">
                  <span className="text-white/60 text-xs">—</span>
                  {t(k)}
                </li>
              ))}
            </ul>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors shrink-0 mt-0.5"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">

        {/* Android */}
          {isAndroid && canNativeInstall && (
            <button
              onClick={onNativeInstall}
              className="w-full flex items-center justify-center gap-2 bg-primary-brown hover:bg-primary-brown/80 text-white font-bold text-sm px-6 py-3.5 rounded-2xl shadow-md shadow-primary-brown/25 transition-all active:scale-95"
            >
              <Download size={18} />
              {t('install.modal.android.cta')}
            </button>
          )}

          {/* Android — no deferred prompt: show manual steps */}
          {isAndroid && !canNativeInstall && (
            <div className="space-y-3">
              <StepRow number={1} icon={<MoreVertical size={16} className="text-primary-brown" />} text={t('install.modal.android.step1')} />
              <StepRow number={2} icon={<Download size={16} className="text-primary-brown" />} text={t('install.modal.android.step2')} />
            </div>
          )}

          {/* iOS */}
          {isIOS && (
            <div className="space-y-3">
              <StepRow number={1} icon={<Share size={16} className="text-primary-brown" />} text={t('install.modal.ios.step1')} />
              <StepRow number={2} icon={<Plus size={16} className="text-primary-brown" />} text={t('install.modal.ios.step2')} />
              <StepRow number={3} icon={<Plus size={16} className="text-primary-brown" />} text={t('install.modal.ios.step3')} />
              <p className="text-xs text-gray-400 bg-primary-brown/8 rounded-xl px-3 py-2 leading-snug mt-1">
                {t('install.modal.ios.hint')}
              </p>
            </div>
          )}

          {/* Desktop */}
          {isDesktop && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 bg-primary-brown/8 rounded-2xl px-4 py-4">
                <div className="w-9 h-9 rounded-full bg-primary-brown/15 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 -960 960 960" width="18" className="text-primary-brown" fill="currentColor"><path d="M240-120v-80l40-40H160q-33 0-56.5-23.5T80-320v-440q0-33 23.5-56.5T160-840h320v80H160v440h640v-120h80v120q0 33-23.5 56.5T800-240H680l40 40v80H240Zm360-240L400-560l56-56 104 103v-327h80v327l104-103 56 56-200 200Z"/></svg>
                </div>
                <p className="text-sm text-gray-700 leading-snug">
                  Look for the&nbsp;<span className="inline-flex items-center justify-center w-5 h-5 rounded bg-primary-brown/15 mx-0.5 align-middle"><svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor" className="text-primary-brown"><path d="M240-120v-80l40-40H160q-33 0-56.5-23.5T80-320v-440q0-33 23.5-56.5T160-840h320v80H160v440h640v-120h80v120q0 33-23.5 56.5T800-240H680l40 40v80H240Zm360-240L400-560l56-56 104 103v-327h80v327l104-103 56 56-200 200Z"/></svg></span>&nbsp;icon in your browser's address bar and click it to install KodaCare.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Dismiss */}
        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full text-sm text-gray-400 hover:text-gray-600 py-2 transition-colors"
          >
            {t('install.modal.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepRow({ number, icon, text }: { number: number; icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-primary-brown text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
        {number}
      </div>
      <p className="text-sm text-gray-700 leading-snug flex-1">{text}</p>
    </div>
  );
}

function ManualStep({ steps }: { icon: React.ReactNode; label: string; steps: { icon: React.ReactNode; text: string }[] }) {
  return (
    <div className="space-y-3">
      {steps.map((s, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-primary-brown/15 flex items-center justify-center shrink-0 mt-0.5">
            {s.icon}
          </div>
          <p className="text-sm text-gray-700 leading-snug flex-1">{s.text}</p>
        </div>
      ))}
    </div>
  );
}
